import { useTranslation } from 'react-i18next';
import { type BetResult } from '@fpsm/cxy-types';

import { type Repub, type RepubConfig, REPUBS } from '@cx-modules/constants';

import { Button, GridTable, Profile, TableGroup, TipsterPerformanceIndicator } from '~/components/cxy/cxy.ts';
import { formatDate } from '~/configuration/utils/formatDate';
import { usePlusConfig } from '~/providers/plus-config-context';

import { trackActiveTipsClick, trackTipsterProfileClick } from '../../../tracking';

import styles from './styles/Tipster.module.css';

export type TipsterProps = {
  hitRate: number;
  last10: BetResult[];
  napOfTheDay?: boolean;
  position: number;
  profileLink: string;
  repub: Repub;
  stats: { lifetime: number; roi: number; year: number };
  tipLink: { href: string; text: string };
  tipsterImage: string;
  tipsterName: string;
  tipsterSubline: string;
  updateDate: string;
};

const formatStatValue = (value: number) => {
  return value > 0 ? `+${value}` : value;
};

const isValidDate = (dateString: string): boolean => {
  if (!dateString) return false;
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

const safeFormatDate = (dateString: string, repubConfig: RepubConfig, tRecently: string): string => {
  if (!isValidDate(dateString)) {
    console.debug(`Invalid date: ${dateString}`);
    return tRecently;
  }
  try {
    return formatDate(dateString, 'EEE, MMM d h:mm a zzz', repubConfig);
  } catch (error) {
    console.error(`Error formatting date: ${dateString}`, error);
    return tRecently;
  }
};

const Tipster = ({
  hitRate,
  last10,
  napOfTheDay = false,
  position,
  profileLink,
  repub,
  stats,
  tipLink,
  tipsterImage,
  tipsterName,
  tipsterSubline,
  updateDate,
}: TipsterProps) => {
  const repubConfig = REPUBS[repub];
  const { tipsters } = usePlusConfig();

  const { t } = useTranslation(['label']);

  return (
    <TableGroup className={styles.tips}>
      <GridTable className={styles.tipsTable} label="Tipster" mobileColumns={3}>
        <GridTable.Caption>
          {t('CX_PLUS_TIPSTERS_UPDATED')} {safeFormatDate(updateDate, repubConfig, t('CX_RECENTLY'))}
        </GridTable.Caption>
        <GridTable.Head className={styles.tableHeadings} row={6}>
          <GridTable.Row>
            <GridTable.Column mobileView="hide">
              {t('CX_PLUS_TIPSTERS_UPDATED')} {safeFormatDate(updateDate, repubConfig, t('CX_RECENTLY'))}
            </GridTable.Column>
            <GridTable.Column mobileView="hide">{t('CX_PLUS_TIPSTERS_LAST_10_SELECTIONS')}</GridTable.Column>
            <GridTable.Column justifyContent="center" showDividers>
              {t('CX_PLUS_TIPSTERS_YTD')}
            </GridTable.Column>
            <GridTable.Column justifyContent="center" showDividers>
              {t('CX_PLUS_TIPSTERS_LIFETIME')}
            </GridTable.Column>
            <GridTable.Column justifyContent="center" showDividers>
              {t('CX_PLUS_TIPSTERS_ROI')}
            </GridTable.Column>
            <GridTable.Column justifyContent="center" mobileView="hide">
              {t('CX_PLUS_TIPSTERS_ACTIVE_TIPS')}
            </GridTable.Column>
          </GridTable.Row>
        </GridTable.Head>
        <GridTable.Body>
          <GridTable.Row>
            <GridTable.Cell className={styles.profileCell}>
              <Profile
                description={tipsterSubline}
                image={tipsterImage}
                name={tipsterName}
                onClick={() => trackTipsterProfileClick({ tipster: tipsterName, sport: tipsterSubline })}
                profileLink={{
                  href: `${profileLink}/bio`,
                  text: t('CX_PLUS_TIPSTERS_VIEW_PROFILE'),
                }}
              />
            </GridTable.Cell>
            <GridTable.Cell isFullWidthMobile>
              <TipsterPerformanceIndicator
                className={styles.indicator}
                hasMaxWidth={false}
                history={last10}
                label={t('CX_PLUS_TIPSTERS_LAST_10_SELECTIONS')}
                position="start"
                spacing="s"
              />
            </GridTable.Cell>
            <GridTable.Cell data-label={t('CX_PLUS_TIPSTERS_YTD')} justifyContent="center" showDividers>
              {formatStatValue(stats.year)} {tipsters?.ui?.pointsUnit}
            </GridTable.Cell>
            <GridTable.Cell data-label={t('CX_PLUS_TIPSTERS_LIFETIME')} justifyContent="center" showDividers>
              {formatStatValue(stats.lifetime)}
            </GridTable.Cell>
            <GridTable.Cell data-label={t('CX_PLUS_TIPSTERS_ROI')} justifyContent="center" showDividers>
              {formatStatValue(stats.roi)}%
            </GridTable.Cell>
            <GridTable.Cell isFullWidthMobile>
              <Button
                asChild
                className={styles.button}
                isFullWidth
                onClick={() =>
                  trackActiveTipsClick({
                    napOfTheDay,
                    selectionRate: `${hitRate}%`,
                    tipster: tipsterName,
                    position,
                    sport: tipsterSubline,
                  })
                }
              >
                <a href={tipLink.href}>{tipLink.text}</a>
              </Button>
            </GridTable.Cell>
          </GridTable.Row>
        </GridTable.Body>
      </GridTable>
    </TableGroup>
  );
};

export { Tipster };
